import { useTheme, type ThemeMode } from '@archetelos/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Tabs from '@radix-ui/react-tabs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { SubscriptionTab } from './SubscriptionTab';

import {
  setReadingPreferences,
  useReadingPreferences,
} from '@/features/subscriber/useReadingPreferences';
import { useAuth } from '@/lib/auth/useAuth';
import { homeQueries, setDigestFrequency } from '@/lib/queries/home';
import { supabase } from '@/lib/supabase';

// Mirrors the profiles_username_format check constraint (lowercase, 3–30,
// starts alphanumeric). Input is lowercased before this is applied.
const USERNAME_RE = /^[a-z0-9][a-z0-9_-]{2,29}$/;

const profileSchema = z.object({
  username: z.string().min(1, 'Required'),
  display_name: z.string().min(1, 'Required').max(60),
  bio: z.string().max(500, 'Keep it under 500 characters').optional().or(z.literal('')),
});
type ProfileValues = z.infer<typeof profileSchema>;

const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

export function SettingsPage() {
  return (
    <div className="mx-auto max-w-3xl">
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Settings</p>
      <h1 className="mt-2 font-serif text-3xl text-ink sm:text-4xl">Your account.</h1>

      <Tabs.Root defaultValue="profile" className="mt-10">
        <Tabs.List className="flex gap-1 border-b border-ink/10">
          <TabTrigger value="profile">Profile</TabTrigger>
          <TabTrigger value="account">Account</TabTrigger>
          <TabTrigger value="preferences">Preferences</TabTrigger>
          <TabTrigger value="notifications">Notifications</TabTrigger>
          <TabTrigger value="subscription">Subscription</TabTrigger>
        </Tabs.List>
        <Tabs.Content value="profile" className="pt-8">
          <ProfileTab />
        </Tabs.Content>
        <Tabs.Content value="account" className="pt-8">
          <AccountTab />
        </Tabs.Content>
        <Tabs.Content value="preferences" className="pt-8">
          <PreferencesTab />
        </Tabs.Content>
        <Tabs.Content value="notifications" className="pt-8">
          <NotificationsTab />
        </Tabs.Content>
        <Tabs.Content value="subscription" className="pt-8">
          <SubscriptionTab />
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
}

function TabTrigger({ value, children }: { value: string; children: React.ReactNode }) {
  return (
    <Tabs.Trigger
      value={value}
      className="-mb-px border-b-2 border-transparent px-4 py-2 font-sans text-sm text-ink/60 data-[state=active]:border-oxblood data-[state=active]:text-ink"
    >
      {children}
    </Tabs.Trigger>
  );
}

function ProfileTab() {
  const { user, profile, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const form = useForm<ProfileValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { username: '', display_name: '', bio: '' },
  });

  useEffect(() => {
    if (profile) {
      form.reset({
        username: profile.username ?? '',
        display_name: profile.display_name ?? '',
        bio: profile.bio ?? '',
      });
    }
  }, [profile, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    if (!user) return;
    const username = values.username.trim().toLowerCase();
    if (!USERNAME_RE.test(username)) {
      form.setError('username', {
        message:
          '3–30 characters: lowercase letters, numbers, _ or -; start with a letter or number',
      });
      return;
    }
    // Only check availability when it actually changed (the RPC counts the
    // caller's own current username as taken).
    if (username !== (profile?.username ?? '')) {
      const { data: available, error: checkError } = await supabase.rpc(
        'check_username_available',
        {
          check_username: username,
        },
      );
      if (checkError) {
        toast.error(checkError.message);
        return;
      }
      if (!available) {
        form.setError('username', { message: 'That username is taken' });
        return;
      }
    }
    const { error } = await supabase
      .from('profiles')
      .update({ username, display_name: values.display_name, bio: values.bio || null })
      .eq('id', user.id);
    if (error) {
      if (error.code === '23505') {
        form.setError('username', { message: 'That username is taken' });
        return;
      }
      toast.error(error.message);
      return;
    }
    await refreshProfile();
    toast.success('Profile updated.');
  });

  const uploadAvatar = async (file: File) => {
    if (!user) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      toast.error('Use a JPEG, PNG, WEBP, or GIF.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('Image must be under 2 MB.');
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split('.').pop() ?? 'jpg';
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;
      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
      const cacheBusted = `${pub.publicUrl}?v=${Date.now()}`;
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: cacheBusted })
        .eq('id', user.id);
      if (updateError) throw updateError;
      await refreshProfile();
      void queryClient.invalidateQueries({ queryKey: ['profile'] });
      toast.success('Avatar updated.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Avatar upload failed.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="flex items-center gap-4">
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt="" className="h-16 w-16 rounded-full object-cover" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-ink/10 text-ink/40">
            {profile?.display_name?.[0]?.toUpperCase() ?? '·'}
          </div>
        )}
        <div>
          <button
            type="button"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
            className="rounded-md border border-ink/20 px-3 py-1.5 text-xs font-medium uppercase tracking-[0.12em] text-ink hover:border-oxblood hover:text-oxblood disabled:opacity-60"
          >
            {uploading ? 'Uploading…' : 'Change avatar'}
          </button>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void uploadAvatar(file);
              e.target.value = '';
            }}
          />
          <p className="mt-1 text-xs text-ink/50">JPG, PNG, WEBP, or GIF — up to 2 MB.</p>
        </div>
      </div>

      <Field
        id="username"
        label="Username"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        error={form.formState.errors.username?.message}
        {...form.register('username')}
      />
      <Field
        id="display_name"
        label="Display name"
        error={form.formState.errors.display_name?.message}
        {...form.register('display_name')}
      />
      <div>
        <label
          htmlFor="bio"
          className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
        >
          Bio
        </label>
        <textarea
          id="bio"
          rows={4}
          {...form.register('bio')}
          className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
        />
        {form.formState.errors.bio && (
          <p role="alert" className="mt-1 text-xs text-signal">
            {form.formState.errors.bio.message}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={form.formState.isSubmitting}
        className="rounded-md bg-ink px-4 py-2 font-sans text-sm font-medium text-parchment hover:bg-oxblood disabled:opacity-60"
      >
        {form.formState.isSubmitting ? 'Saving…' : 'Save profile'}
      </button>
    </form>
  );
}

function PreferencesTab() {
  const { user, profile, refreshProfile } = useAuth();
  const { theme, setTheme } = useTheme();
  const [saving, setSaving] = useState(false);

  const updateTheme = async (next: ThemeMode) => {
    setTheme(next);
    if (!user) return;
    setSaving(true);
    const { error } = await supabase
      .from('profiles')
      .update({ theme_preference: next })
      .eq('id', user.id);
    setSaving(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    await refreshProfile();
  };

  const current = (profile?.theme_preference as ThemeMode | undefined) ?? theme;

  return (
    <div className="space-y-8">
      <section>
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Theme</p>
        <div className="mt-3 flex gap-2">
          {(['light', 'dark', 'auto'] as const).map((option) => (
            <button
              key={option}
              type="button"
              disabled={saving}
              onClick={() => void updateTheme(option)}
              className={`rounded-md border px-4 py-2 text-sm transition-colors ${
                current === option
                  ? 'border-oxblood bg-oxblood/10 text-oxblood'
                  : 'border-ink/20 text-ink hover:border-oxblood'
              }`}
              aria-pressed={current === option}
            >
              {option[0]!.toUpperCase() + option.slice(1)}
            </button>
          ))}
        </div>
        <p className="mt-2 text-xs text-ink/50">
          Auto matches the current reading surface — light for editorial, dark for video, system
          preference for the app shell.
        </p>
      </section>

      <ReadingPreferencesSection />
    </div>
  );
}

function ReadingPreferencesSection() {
  const prefs = useReadingPreferences();
  return (
    <section>
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Reading</p>
      <div className="mt-3 space-y-4">
        <div>
          <label className="font-sans text-sm text-ink/80">
            Font size
            <input
              type="range"
              min={0.875}
              max={1.5}
              step={0.0625}
              value={prefs.fontScale}
              onChange={(e) => setReadingPreferences({ fontScale: Number(e.target.value) })}
              className="mt-2 block w-full max-w-xs accent-oxblood"
            />
          </label>
          <p className="text-xs text-ink/50">{prefs.fontScale.toFixed(3)} rem</p>
        </div>
        <div>
          <label className="font-sans text-sm text-ink/80">
            Line height
            <input
              type="range"
              min={1.4}
              max={2.1}
              step={0.05}
              value={prefs.lineHeight}
              onChange={(e) => setReadingPreferences({ lineHeight: Number(e.target.value) })}
              className="mt-2 block w-full max-w-xs accent-oxblood"
            />
          </label>
          <p className="text-xs text-ink/50">{prefs.lineHeight.toFixed(2)}</p>
        </div>
        <p className="text-xs text-ink/50">
          Applies to the in-app article reader. Saved on this device.
        </p>
      </div>
    </section>
  );
}

function AccountTab() {
  return (
    <div className="space-y-6">
      <section className="rounded-md border border-ink/10 bg-parchment p-6">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Password</p>
        <p className="mt-3 font-sans text-sm text-ink/70">Change your password any time.</p>
        <Link
          to="/auth/update-password"
          className="mt-4 inline-block rounded-md border border-ink/20 px-4 py-2 font-sans text-sm font-medium text-ink hover:border-oxblood hover:text-oxblood"
        >
          Change password →
        </Link>
      </section>
      <section className="rounded-md border border-ink/10 bg-parchment p-6">
        <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Email</p>
        <p className="mt-3 font-sans text-sm text-ink/70">Email management is coming.</p>
      </section>
    </div>
  );
}

function NotificationsTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: prefs } = useQuery(homeQueries.preferences(user?.id));
  const [saving, setSaving] = useState(false);
  // Default to weekly (onboarding's default) when nothing is stored yet.
  const current =
    (prefs?.email_digest_frequency as 'weekly' | 'monthly' | 'never' | null) ?? 'weekly';

  const choose = async (freq: 'weekly' | 'monthly' | 'never') => {
    if (!user) return;
    setSaving(true);
    try {
      await setDigestFrequency(user.id, freq);
      await queryClient.invalidateQueries({ queryKey: ['home', 'preferences', user.id] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save preference.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-md border border-ink/10 bg-parchment p-6">
      <p className="font-sans text-xs uppercase tracking-[0.18em] text-ink/50">Email digest</p>
      <div className="mt-3 flex gap-2">
        {(['weekly', 'monthly', 'never'] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            disabled={saving}
            onClick={() => void choose(opt)}
            aria-pressed={current === opt}
            className={`rounded-md border px-4 py-2 text-sm transition-colors ${
              current === opt
                ? 'border-oxblood bg-oxblood/10 text-oxblood'
                : 'border-ink/20 text-ink hover:border-oxblood'
            }`}
          >
            {opt[0]!.toUpperCase() + opt.slice(1)}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-ink/50">
        Digests haven&apos;t started sending yet — your choice is honored when they do.
      </p>
    </div>
  );
}

interface FieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  error?: string;
}
const Field = ({ id, label, error, ...rest }: FieldProps) => (
  <div>
    <label
      htmlFor={id}
      className="mb-1 block font-sans text-xs uppercase tracking-[0.12em] text-ink/60"
    >
      {label}
    </label>
    <input
      id={id}
      {...rest}
      aria-invalid={Boolean(error)}
      className="w-full rounded-md border border-ink/20 bg-parchment px-3 py-2 text-sm text-ink focus:border-oxblood focus:outline-none focus:ring-2 focus:ring-oxblood/30"
    />
    {error && (
      <p role="alert" className="mt-1 text-xs text-signal">
        {error}
      </p>
    )}
  </div>
);
